import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import FormProvider from 'src/components/hook-form';
import { FormStepper } from 'src/components/stepper';
import { translateKey } from 'src/locales/translate';
import UnconfirmedSaveBanner from 'src/sections/@dashboard/warehouse/components/UnconfirmedSaveBanner';
import { useDemandRequestModal } from 'src/sections/@dashboard/warehouse/hooks/useDemandRequestModal';
import {
  CreateReminderDtoCustomRecurrenceUnitEnum,
  CreateReminderDtoRecurrenceTypeEnum,
  GetMedicineDto,
  GetProfileMedicationsDto,
  GetProfileMedicationsDtoLinkedReminder,
} from 'woofwoof-api';
import MedicationFormActions from './MedicationFormActions';
import MedicationFormStepBody from './MedicationFormStepBody';
import MedicationHistoryTimeline from './MedicationHistoryTimeline';
import { DosageAutofillPick } from './dosageAutofill';
import {
  buildMedicationFormDefaults,
  MedicationFormInitialValues,
} from './medicationFormDefaults';
import {
  MedicationsFormModalValuesProps,
  medicationFormResolver,
} from './medicationFormSchema';
import { buildMedicationSteps } from './medicationFormSteps';
import { MedicationStockFormValues } from './medicationStockValues';
import { StockSectionVariant } from './medicationStockView';
import { useMedicationFormSteps } from './useMedicationFormSteps';
import { useMedicationFormSubmit } from './useMedicationFormSubmit';
import { useOwnerAddress } from './useOwnerAddress';
import { useSelectedMedicine } from './useSelectedMedicine';
import { useStockRefusalField } from './useStockRefusalField';
import { useDosageAutofill } from './useDosageAutofill';

export type { MedicationsFormModalValuesProps } from './medicationFormSchema';
export type { MedicationFormInitialValues } from './medicationFormDefaults';

export interface MedicationFormModalProps {
  isModalOpen: boolean;
  closeModal: () => void;
  medication?: GetProfileMedicationsDto;
  linkedReminder?: GetProfileMedicationsDtoLinkedReminder | null;
  /** `recordChanged`: false when only the status moved. */
  onSubmit: (
    data: MedicationsFormModalValuesProps,
    meta: { recordChanged: boolean },
  ) => void;
  onDelete?: () => void;
  isUpdating?: boolean;
  isCreating?: boolean;
  isDeleting?: boolean;
  canEdit?: boolean;
  isViewOnly?: boolean;
  /** When true, hides the entire reminder section. Defaults to false. */
  hideReminder?: boolean;
  singleReminderOnly?: boolean;
  /** Hides status and clinical fields where they make no sense (treatments). */
  hideLifecycle?: boolean;
  /**
   * The medication's visit is past its edit window: the form offers only the
   * status, which still moves, and nothing can be removed (#707).
   */
  statusOnly?: boolean;
  /** The patient, so the summary can read this medication's history. */
  profileId?: string;
  initialMedicine?: GetMedicineDto | null;
  initialValues?: MedicationFormInitialValues;
  initialMedicineName?: string;
  initialMedicineProductId?: string;
  /**
   * When true, shows the delete button even when medication is not provided.
   * Also changes the dialog title to "Edytuj lekarstwo".
   */
  showDeleteButton?: boolean;
  /** Facility screens: a Warehouse pick adds the "Magazyn" step. Never on the owner's tab. */
  stockEnabled?: boolean;
  /** Phase 4: "Zgłoś zapotrzebowanie" from the shortage warning. */
  onRequestStockDemand?: (productId: string) => void;
  /** Stock of a row being edited outside a saved medication (treatment lines). */
  initialStock?: MedicationStockFormValues;
  /** Treatment rows (F-7): a drug line hides "Partia", a template also the recipient. */
  stockVariant?: StockSectionVariant;
  /** R3-4: the host's write series is unresolved: banner, locked fields, resend. */
  unconfirmedSave?: boolean;
  /** F-8/R-C: that series is held by 422 WAREHOUSE_DISABLED; the banner says so. */
  unconfirmedBlocked?: boolean;
  /** R3-4: the frozen request's values, shown when the form reopens pending. */
  unconfirmedValues?: MedicationsFormModalValuesProps;
  /** #688 R1-2: the host's last failed save; RECIPIENT_ADDRESS_REQUIRED marks its field. */
  stockWriteError?: unknown;
  /**
   * #688 R1-2: the patient whose owner's already loaded address starts a new
   * Rx supply's "Adres odbiorcy"; defaults to `profileId`.
   */
  ownerProfileId?: string;
  /**
   * #692: the patient the drug is for, where the host passes neither
   * `profileId` nor `ownerProfileId` (the visit's "Dodaj do wizyty" menu).
   * The dosage autofill reads the clinic's earlier doses for its species.
   */
  patientId?: string;
  /** #692 §4: a treatment line being edited keeps its drug; step 1 shows it read-only. */
  medicineLocked?: boolean;
  /**
   * #692 R1-2: a drug the host picked just before opening a new entry (a
   * treatment line's "Dodaj lek"). Once the form is hydrated it prefills the
   * dose as a pick in step 1 would; never passed when reopening an entry.
   */
  newMedicinePick?: DosageAutofillPick;
}

export default function MedicationFormModal({
  isModalOpen,
  closeModal,
  medication,
  linkedReminder,
  onSubmit,
  isUpdating,
  isCreating,
  isDeleting,
  onDelete,
  canEdit = true,
  isViewOnly = false,
  hideReminder = false,
  singleReminderOnly = false,
  hideLifecycle = false,
  statusOnly = false,
  profileId,
  initialMedicine,
  initialValues,
  initialMedicineName,
  initialMedicineProductId,
  showDeleteButton = false,
  stockEnabled = false,
  onRequestStockDemand,
  initialStock,
  stockVariant,
  unconfirmedSave = false,
  unconfirmedBlocked = false,
  unconfirmedValues,
  stockWriteError,
  ownerProfileId,
  patientId,
  medicineLocked = false,
  newMedicinePick,
}: MedicationFormModalProps) {
  /**
   * The record the modal is showing, held until the dialog has finished
   * animating out.
   *
   * Callers clear their selected id the moment they close this, which used to
   * take the medication out from under a dialog still on screen: `isEditing`
   * flipped, the steps rebuilt without the status one, the active index fell
   * off the end and the body rendered nothing — a modal collapsing to a sliver
   * as it faded.
   */
  const [shownMedication, setShownMedication] = useState(medication);

  useEffect(() => {
    if (medication) setShownMedication(medication);
  }, [medication]);

  // The prop wins while it has a value: state lands a render late, and that
  // render painted "Dodaj lekarstwo" with the stepper before flipping to the
  // summary — a visible blink on every open. The held copy is only for the
  // other direction, when the caller has already cleared its selection.
  const activeMedication = medication ?? shownMedication;
  const isEditing = Boolean(activeMedication);
  const canEditReminder = canEdit && (linkedReminder?.canEdit ?? true);
  // Locked in flight and while unresolved (R3-4); a close in flight is ignored.
  const isDisabled = !canEdit || isUpdating || isCreating || unconfirmedSave;
  const close = isCreating || isUpdating ? () => undefined : closeModal;

  // Owned here, not by the picker step: the leaflet is shown above every step,
  // and editing lets a vet jump straight to the dose without the picker
  // mounting at all.
  /**
   * Which medication the vet has pressed "Edytuj" on. Preview is derived from
   * it rather than held as its own state: state settles a render late, and
   * that render showed the form before flipping to the summary.
   */
  const [editingId, setEditingId] = useState<string | null>(null);
  const isPreview = isEditing && editingId !== activeMedication?.id;

  /** Which half of the summary is showing: the record, or what happened to it. */
  const [previewTab, setPreviewTab] = useState<'details' | 'history'>(
    'details',
  );

  const { selectedMedicine, setSelectedMedicine } = useSelectedMedicine(
    activeMedication?.medicineProductId ?? initialMedicineProductId,
    isModalOpen,
  );
  const closingResetTimer = useRef<ReturnType<typeof setTimeout>>();
  /** #692: one per open, so a default set for one line never reaches the next. */
  const [formSession, setFormSession] = useState(0);
  const demand = useDemandRequestModal();

  const builtDefaultValues = buildMedicationFormDefaults({
    medication: activeMedication,
    linkedReminder,
    initialMedicine: !activeMedication ? initialMedicine : null,
    initialMedicineName,
    initialValues,
    hideReminder,
    initialStock,
  });
  const defaultValues = singleReminderOnly
    ? {
        ...builtDefaultValues,
        reminderRecurrenceType: CreateReminderDtoRecurrenceTypeEnum.Single,
        reminderCustomEvery: 1,
        reminderCustomUnit: CreateReminderDtoCustomRecurrenceUnitEnum.Week,
        reminderCustomDayOfWeek: '' as const,
        reminderCustomWeekOfMonth: '' as const,
      }
    : builtDefaultValues;

  const methods = useForm<MedicationsFormModalValuesProps>({
    resolver: medicationFormResolver,
    defaultValues,
  });

  const { reset, watch, setValue, getValues, trigger } = methods;
  const reminderEnabled = watch('reminderEnabled');
  // #692 rev B: a Product in the form — a Warehouse pick, or a saved
  // taking — adds "Magazyn" before the summary.
  const stockProductId = watch('stockProductId');
  const withStock = stockEnabled && Boolean(stockProductId);

  const steps = useMemo(
    () =>
      buildMedicationSteps({
        hideLifecycle,
        hideReminder,
        isEditing,
        withStock,
        statusOnly,
      }),
    [hideLifecycle, hideReminder, isEditing, withStock, statusOnly],
  );
  const {
    activeStep,
    completedSteps,
    isLastStep,
    goNext,
    goBack,
    goToStep,
    goToFirstError,
  } = useMedicationFormSteps({
    steps,
    trigger,
    isModalOpen,
    isEditing,
  });
  const step = steps[activeStep];
  const submit = useMedicationFormSubmit({
    methods,
    defaultValues,
    onSubmit,
    singleReminderOnly,
    unconfirmedSave,
    goToFirstError,
  });
  useStockRefusalField(stockWriteError, methods.setError, goToFirstError);
  const ownerAddress = useOwnerAddress(
    stockEnabled ? ownerProfileId ?? profileId : undefined,
  );
  const autofill = useDosageAutofill({
    methods,
    isOpen: isModalOpen,
    patientId: patientId ?? profileId ?? ownerProfileId,
    disabled: isDisabled,
  });
  // Facility screens only: nothing on the owner's tab is prefilled.
  const dosageAutofill = stockEnabled ? autofill : undefined;

  /**
   * The closing reset is deferred so the fields don't visibly empty while the
   * dialog animates out. That timer used to be fire-and-forget, including the
   * one scheduled on mount — open the modal within its 500ms and it landed on
   * the freshly hydrated form and blanked it. The vet then typed into a form
   * that no longer knew which medication it was, and the save did nothing
   * visible: #89's "edit intermittently does not persist", which only showed
   * up under load because that is when the modal is opened quickly enough.
   * Keeping the handle and clearing it on every run is the whole fix.
   *
   * Keyed on the medication's id too: the modal can open before its row has
   * arrived from the server, and must re-hydrate when it does.
   */
  useEffect(() => {
    if (closingResetTimer.current) {
      clearTimeout(closingResetTimer.current);
      closingResetTimer.current = undefined;
    }

    if (isModalOpen) {
      reset(defaultValues);
      setFormSession((session) => session + 1);
      // R3-4: reopened on what it will resend; the defaults stay the saved state.
      if (unconfirmedSave && unconfirmedValues) {
        reset(unconfirmedValues, { keepDefaultValues: true });
      }
      if (newMedicinePick && !activeMedication && !isDisabled) {
        dosageAutofill?.applyPick(newMedicinePick);
      }
      if (initialMedicine && !activeMedication) {
        setSelectedMedicine(initialMedicine);
      }
      return undefined;
    }

    closingResetTimer.current = setTimeout(() => {
      reset({ ...defaultValues, reminderEnabled: false });
      setSelectedMedicine(null);
      setShownMedication(undefined);
      setEditingId(null);
      setPreviewTab('details');
    }, 500);

    return () => {
      if (closingResetTimer.current) clearTimeout(closingResetTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, activeMedication?.id, reset, singleReminderOnly]);

  // Seed the reminder description from what the vet has already typed, once.
  useEffect(() => {
    if (!reminderEnabled || linkedReminder) return;
    if (getValues('reminderDescription')) return;
    const currentName = getValues('name') || selectedMedicine?.productName;
    if (!currentName) return;
    const quantity = getValues('quantityValue');
    const parts = [
      currentName,
      quantity ? `Dawka: ${quantity}` : undefined,
      getValues('notes'),
    ].filter(Boolean);
    setValue('reminderDescription', parts.join(' - '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderEnabled]);

  const title = isPreview
    ? translateKey('medication.form.detailsTitle')
    : statusOnly
    ? translateKey('medication.form.changeStatusTitle')
    : isEditing || showDeleteButton
    ? 'Edytuj lekarstwo'
    : 'Dodaj lekarstwo';

  /**
   * On the first step of an edit, back means back to the summary the modal
   * opened on — not out of the modal.
   */
  const handleBack = () => {
    if (isEditing && activeStep === 0) {
      setEditingId(null);
      return;
    }
    goBack();
  };

  return (
    <Dialog
      open={isModalOpen}
      onClose={close}
      // `PaperProps`, not `slotProps.paper`: the latter left the paper's
      // default margin in place, costing 16px of a phone's width against the
      // schedule-visit modal this is meant to match.
      PaperProps={{
        sx: {
          width: { xs: 'calc(100vw - 32px)', sm: 'calc(100vw - 48px)' },
          maxWidth: '670px',
          mx: 0,
          my: { xs: 2, sm: 4 },
        },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
      >
        {title}
        <Box flex={1} />
      </DialogTitle>
      <FormProvider methods={methods} onSubmit={submit}>
        <DialogContent>
          <UnconfirmedSaveBanner
            pending={unconfirmedSave}
            message={
              unconfirmedBlocked
                ? translateKey('warehouse.errors.WAREHOUSE_DISABLED')
                : undefined
            }
          />
          {statusOnly && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              data-testid="medication-status-only-notice"
            >
              {translateKey('medication.form.statusOnlyNotice')}
            </Alert>
          )}

          {isPreview && (
            <Tabs
              value={previewTab}
              onChange={(event, tab) => setPreviewTab(tab)}
              // The rule under the strip, as every other tab set in the
              // dashboard draws it.
              sx={{
                mb: 2,
                borderWidth: '1px',
                borderColor: 'divider',
                borderBottomStyle: 'solid',
              }}
            >
              <Tab
                value="details"
                label={translateKey('medication.history.tabDetails')}
                data-testid="medication-tab-details"
              />
              <Tab
                value="history"
                label={translateKey('medication.history.tabHistory')}
                data-testid="medication-tab-history"
              />
            </Tabs>
          )}

          {!isPreview && (
            <FormStepper
              steps={steps}
              activeStep={activeStep}
              completedSteps={completedSteps}
              allStepsReachable={isEditing}
              onStepClick={goToStep}
            />
          )}

          {isPreview && previewTab === 'history' ? (
            <MedicationHistoryTimeline
              medicationId={activeMedication?.id}
              profileId={profileId}
            />
          ) : (
            <MedicationFormStepBody
              stepId={isPreview ? 'summary' : step?.id}
              disabled={isDisabled}
              onMedicineChange={setSelectedMedicine}
              selectedMedicine={selectedMedicine}
              canEditReminder={canEditReminder}
              singleReminderOnly={singleReminderOnly}
              isViewOnly={isViewOnly}
              hasLinkedReminder={Boolean(linkedReminder)}
              isEditing={isEditing}
              creatorName={activeMedication?.creatorName}
              creatorOrganizationName={
                activeMedication?.creatorOrganizationName
              }
              creatorAvatar={activeMedication?.creatorAvatar}
              hideLifecycle={hideLifecycle}
              hideReminder={hideReminder}
              stockEnabled={stockEnabled}
              onRequestStockDemand={
                onRequestStockDemand ??
                (stockEnabled ? demand.openForProductId : undefined)
              }
              onRequestNamedDemand={
                stockEnabled ? demand.openForName : undefined
              }
              stockVariant={stockVariant}
              stockOwnerAddress={ownerAddress}
              autofill={dosageAutofill}
              medicineLocked={medicineLocked}
              formSession={formSession}
            />
          )}
        </DialogContent>

        <MedicationFormActions
          isPreview={isPreview}
          onStartEditing={() => {
            setEditingId(activeMedication?.id ?? null);
            goToStep(0);
          }}
          activeStep={activeStep}
          isLastStep={isLastStep || unconfirmedSave}
          onNext={goNext}
          onBack={handleBack}
          closeModal={close}
          onDelete={statusOnly ? undefined : onDelete}
          canEdit={canEdit}
          editLabel={
            statusOnly
              ? translateKey('medication.form.changeStatus')
              : undefined
          }
          isEditing={isEditing}
          showDeleteButton={showDeleteButton}
          isCreating={isCreating}
          isUpdating={isUpdating}
          isDeleting={isDeleting}
        />
      </FormProvider>
      {/* Outside the form: React bubbles the dialog's submit up this tree. */}
      {demand.modal}
    </Dialog>
  );
}
